"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  ThemeIcon,
  List,
  ColorSwatch,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCheck,
  IconX,
  IconCoin,
  IconShirt,
  IconMoodSmile,
  IconQuestionMark,
  IconTarget,
} from "@tabler/icons-react";
import Link from "next/link";

export default function IsOnlineColorAnalysisWorthItContent() {
  // Visual example: wrong colors vs right colors
  const wrongColorsExample = [
    { hex: "#FFA500", name: "Orange" },
    { hex: "#DAA520", name: "Goldenrod" },
    { hex: "#F4A460", name: "Sandy Brown" },
  ];

  const rightColorsExample = [
    { hex: "#E0115F", name: "Ruby" },
    { hex: "#4169E1", name: "Royal Blue" },
    { hex: "#228B22", name: "Forest" },
  ];

  // Full palette preview
  const fullPalettePreview = [
    "#FBCEB1", "#F88379", "#C4E1C1", "#B3E3E2",
    "#FFE28A", "#FADADD", "#D6C9FF", "#87CEEB",
    "#98FF98", "#C8A2C8", "#CCCCFF", "#F5F5DC"
  ];
  const whatYouGet = [
    {
      title: "Your Seasonal Color Type",
      description:
        "One of 12 seasonal palettes (e.g., Soft Autumn, Cool Winter) based on your undertone, contrast, and depth.",
      icon: IconTarget,
    },
    {
      title: "Best Colors Palette",
      description:
        "Specific colors that will make your skin look healthy, your eyes brighter, and your overall appearance more vibrant.",
      icon: IconShirt,
    },
    {
      title: "Colors to Avoid",
      description:
        "Colors that clash with your natural coloring and make you look washed out, tired, or sallow.",
      icon: IconX,
    },
    {
      title: "Practical Application",
      description:
        "Guidance on how to use your colors for clothing, makeup, hair color choices, and accessories.",
      icon: IconMoodSmile,
    },
  ];

  const worthItFor = [
    "You've wasted money on clothes that looked good online but terrible on you",
    "You feel like nothing in your closet looks quite right",
    "You want to simplify shopping by knowing exactly what to look for",
    "You're curious about color seasons but don't want to spend $200+",
    "You've tried guessing your season but keep getting confused",
    "You want to look put-together with less effort",
  ];

  const maybeNotWorthIt = [
    "You already know your colors and just want confirmation (try the free quiz first)",
    "You have very unusual coloring and need expert evaluation",
    "You want a full styling session with wardrobe audit and personal shopping",
    "You prefer the experience of working with someone in person",
  ];

  const realResults = [
    {
      before: "Buying black because it's 'safe'",
      after: "Knowing if black actually works for you or if navy/charcoal is better",
    },
    {
      before: "Avoiding color because you don't know what suits you",
      after: "Confidently wearing your best shades of blue, green, pink, etc.",
    },
    {
      before: "Random makeup purchases that don't look right",
      after: "Choosing lipstick, eyeshadow, and blush that complement your skin",
    },
    {
      before: "Hair color experiments that go wrong",
      after: "Understanding which hair shades will enhance vs. clash",
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            Is Online Color Analysis Worth It?
          </Title>
          <Text size="lg" c="dimmed" maw={650} mx="auto" mb="xl">
            Honest answer: For most people, yes — if you actually use the results.
            Here's a realistic breakdown of what you get and whether it makes sense for you.
          </Text>
        </Box>

        {/* Direct Answer */}
        <Card withBorder p="xl" radius="md" bg="green.0">
          <Group align="flex-start" gap="md">
            <ThemeIcon size={50} radius="xl" color="green" variant="light">
              <IconCheck size={28} />
            </ThemeIcon>
            <Box>
              <Title order={2} size="h3" mb="sm">
                The Short Answer
              </Title>
              <Text size="md">
                At $5-15, online color analysis costs less than one wrong clothing purchase.
                If you've ever bought something that looked great in the store but wrong on you,
                color analysis pays for itself the first time it prevents that mistake.
              </Text>
            </Box>
          </Group>
        </Card>

        {/* What You Actually Get */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            What You Actually Get
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {whatYouGet.map((item, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Group align="flex-start" gap="md">
                  <ThemeIcon color="violet" variant="light" size="lg">
                    <item.icon size={20} />
                  </ThemeIcon>
                  <Box style={{ flex: 1 }}>
                    <Text fw={600} mb="xs">
                      {item.title}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {item.description}
                    </Text>
                  </Box>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Visual Before/After with Colors */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            The Difference Right Colors Make
          </Title>
          <Text size="md" ta="center" maw={600} mx="auto" mb="lg" c="dimmed">
            Wrong colors drain you. Right colors make you look vibrant. Here's an example for someone with cool undertones:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" mb="xl">
            <Paper withBorder p="lg" radius="md" bg="red.0">
              <Text fw={600} ta="center" mb="sm" c="red">Wrong Colors</Text>
              <Text size="sm" c="dimmed" ta="center" mb="md">
                These warm tones clash with cool skin, making it look sallow or tired
              </Text>
              <Group justify="center" gap="xs">
                {wrongColorsExample.map((color, i) => (
                  <Box key={i} ta="center">
                    <ColorSwatch color={color.hex} size={50} radius="md" />
                    <Text size="xs" c="dimmed" mt={4}>{color.name}</Text>
                  </Box>
                ))}
              </Group>
            </Paper>
            <Paper withBorder p="lg" radius="md" bg="green.0">
              <Text fw={600} ta="center" mb="sm" c="green">Right Colors</Text>
              <Text size="sm" c="dimmed" ta="center" mb="md">
                These cool tones harmonize with the skin, making it look healthy and glowing
              </Text>
              <Group justify="center" gap="xs">
                {rightColorsExample.map((color, i) => (
                  <Box key={i} ta="center">
                    <ColorSwatch color={color.hex} size={50} radius="md" />
                    <Text size="xs" c="dimmed" mt={4}>{color.name}</Text>
                  </Box>
                ))}
              </Group>
            </Paper>
          </SimpleGrid>

          <Title order={3} size="h4" mb="md" ta="center">
            What Changes When You Know Your Colors
          </Title>
          <Stack gap="md">
            {realResults.map((item, index) => (
              <Paper key={index} withBorder p="md" radius="md">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <Box>
                    <Text size="xs" c="red" fw={500} mb="xs">
                      BEFORE
                    </Text>
                    <Text size="sm">{item.before}</Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="green" fw={500} mb="xs">
                      AFTER
                    </Text>
                    <Text size="sm">{item.after}</Text>
                  </Box>
                </SimpleGrid>
              </Paper>
            ))}
          </Stack>
        </Card>

        {/* Worth It If / Maybe Not If */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Card withBorder p="xl" radius="md" bg="green.0">
            <Group mb="md">
              <ThemeIcon color="green" variant="light" size="lg">
                <IconCheck size={20} />
              </ThemeIcon>
              <Title order={3} size="h4">
                Worth It If...
              </Title>
            </Group>
            <List size="sm" spacing="sm" icon={<IconCheck size={14} color="green" />}>
              {worthItFor.map((item, index) => (
                <List.Item key={index}>{item}</List.Item>
              ))}
            </List>
          </Card>

          <Card withBorder p="xl" radius="md" bg="orange.0">
            <Group mb="md">
              <ThemeIcon color="orange" variant="light" size="lg">
                <IconQuestionMark size={20} />
              </ThemeIcon>
              <Title order={3} size="h4">
                Maybe Skip If...
              </Title>
            </Group>
            <List size="sm" spacing="sm" icon={<IconX size={14} color="orange" />}>
              {maybeNotWorthIt.map((item, index) => (
                <List.Item key={index}>{item}</List.Item>
              ))}
            </List>
          </Card>
        </SimpleGrid>

        {/* What You Get - Visual Palette */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Title order={2} mb="md" ta="center">
            What $5-15 Gets You
          </Title>
          <Text size="md" ta="center" c="dimmed" mb="lg" maw={500} mx="auto">
            Your complete personalized color palette — 60+ colors with hex codes you can reference anywhere.
          </Text>
          <Paper withBorder p="lg" radius="md" bg="white" maw={600} mx="auto">
            <Text fw={600} ta="center" mb="md">Sample Result: Light Spring Palette</Text>
            <SimpleGrid cols={6} spacing="xs" mb="md">
              {fullPalettePreview.map((hex, i) => (
                <Box key={i} ta="center">
                  <ColorSwatch color={hex} size={36} radius="md" mx="auto" />
                  <Text size="xs" c="dimmed" mt={4}>{hex}</Text>
                </Box>
              ))}
            </SimpleGrid>
            <Text size="xs" c="dimmed" ta="center">
              Plus colors to avoid, makeup recommendations, and outfit ideas tailored to your season
            </Text>
          </Paper>
        </Card>

        {/* Cost Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Cost Perspective
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Paper withBorder p="lg" radius="md" ta="center">
              <ThemeIcon size={50} radius="xl" color="green" variant="light" mx="auto" mb="sm">
                <IconCoin size={28} />
              </ThemeIcon>
              <Text fw={600} mb="xs">Online AI Analysis</Text>
              <Text size="xl" fw={700} c="green">$5-15</Text>
              <Text size="xs" c="dimmed" mt="xs">
                Instant results, retake anytime
              </Text>
            </Paper>
            <Paper withBorder p="lg" radius="md" ta="center">
              <ThemeIcon size={50} radius="xl" color="orange" variant="light" mx="auto" mb="sm">
                <IconCoin size={28} />
              </ThemeIcon>
              <Text fw={600} mb="xs">One Wrong Purchase</Text>
              <Text size="xl" fw={700} c="orange">$30-100+</Text>
              <Text size="xs" c="dimmed" mt="xs">
                That top that looked great online...
              </Text>
            </Paper>
            <Paper withBorder p="lg" radius="md" ta="center">
              <ThemeIcon size={50} radius="xl" color="red" variant="light" mx="auto" mb="sm">
                <IconCoin size={28} />
              </ThemeIcon>
              <Text fw={600} mb="xs">In-Person Consultation</Text>
              <Text size="xl" fw={700} c="red">$150-400+</Text>
              <Text size="xs" c="dimmed" mt="xs">
                Full experience with styling advice
              </Text>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* The Key Question */}
        <Card withBorder p="xl" radius="md" bg="blue.0">
          <Title order={2} mb="md" ta="center">
            The Real Question
          </Title>
          <Text size="lg" ta="center" maw={600} mx="auto">
            The value isn't in the analysis itself — it's in whether you'll actually use it.
            If you take your color palette and apply it to shopping, you'll save money and look better.
            If you get the results and forget about them, any price is wasted.
          </Text>
        </Card>

        {/* Try Free First */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Not Sure? Try Free First
          </Title>
          <Text size="md" ta="center" maw={600} mx="auto" mb="lg">
            Our free color analysis quiz will give you a preliminary season suggestion
            and help you understand the system. If you want confirmation with AI precision,
            you can upgrade to photo analysis after.
          </Text>
          <Flex gap="md" justify="center" wrap="wrap">
            <Button
              size="lg"
              variant="outline"
              component={Link}
              href="/color-analysis-quiz"
              color="violet"
            >
              Take Free Quiz First
            </Button>
          </Flex>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Ready to Find Your Colors?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get your seasonal palette in under a minute.
              Less than the cost of a coffee, useful for every shopping trip.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Get My Color Analysis
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Try Free Quiz First
              </Button>
            </Flex>
            <Text size="sm" c="dimmed">
              Still researching? Learn{" "}
              <Text component={Link} href="/does-ai-color-analysis-work" c="violet" td="underline">
                how AI color analysis works
              </Text>{" "}
              or compare{" "}
              <Text component={Link} href="/ai-vs-in-person-color-analysis" c="violet" td="underline">
                AI vs in-person analysis
              </Text>
              .
            </Text>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
